import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import imageCompression from 'browser-image-compression';
import Modal from './Modal';
import { RestaurantResponse } from '../../types/restaurants';
import { uploadService } from '../../services/uploadService';
import { imageIdToUrl } from '../../utils/imageUtils';

interface EditRestaurantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name?: string;
    description?: string;
    isOpen?: boolean;
    workStartTime?: string;
    workEndTime?: string;
    bannerId?: string;
  }) => void;
  restaurant: RestaurantResponse;
  loading?: boolean;
}

const EditRestaurantModal: React.FC<EditRestaurantModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  restaurant,
  loading = false
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isOpenStatus, setIsOpenStatus] = useState(false);
  const [workStartTime, setWorkStartTime] = useState('');
  const [workEndTime, setWorkEndTime] = useState('');
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && restaurant) {
      setName(restaurant.name);
      setDescription(restaurant.description);
      setIsOpenStatus(restaurant.isOpen);
      setWorkStartTime(restaurant.workStartTime);
      setWorkEndTime(restaurant.workEndTime);
      setBannerFile(null);
      setBannerPreview(null);
      setUploadError('');
    }
  }, [isOpen, restaurant]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setUploadError(t('modals.editRestaurant.invalidFileType'));
        return;
      }

      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setUploadError(t('modals.editRestaurant.fileTooLarge'));
        return;
      }

      setBannerFile(file);
      setUploadError('');

      // Create preview from original file
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setBannerFile(null);
    setBannerPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const updates: any = {};

    if (name !== restaurant.name && name.trim()) {
      updates.name = name.trim();
    }
    if (description !== restaurant.description) {
      updates.description = description.trim();
    }
    if (isOpenStatus !== restaurant.isOpen) {
      updates.isOpen = isOpenStatus;
    }
    if (workStartTime !== restaurant.workStartTime && workStartTime) {
      updates.workStartTime = workStartTime;
    }
    if (workEndTime !== restaurant.workEndTime && workEndTime) {
      updates.workEndTime = workEndTime;
    }

    // Upload banner image if a new one is selected
    if (bannerFile) {
      setUploading(true);
      setUploadError('');

      try {
        // Compress the image before uploading
        const options = {
          maxSizeMB: 1, // Maximum file size in MB
          maxWidthOrHeight: 1920, // Maximum width or height
          useWebWorker: true, // Use web worker for better performance
          fileType: 'image/jpeg' as const, // Convert to JPEG for better compression
        };

        const compressedFile = await imageCompression(bannerFile, options);
        const response = await uploadService.uploadImage(compressedFile);

        if (response.success && response.data) {
          updates.bannerId = response.data.id;
        } else {
          setUploadError(response.error || t('modals.editRestaurant.uploadFailed'));
          setUploading(false);
          return;
        }
      } catch (error) {
        setUploadError(t('modals.editRestaurant.uploadFailed'));
        setUploading(false);
        return;
      } finally {
        setUploading(false);
      }
    }

    if (Object.keys(updates).length > 0) {
      onSubmit(updates);
    }
  };

  const handleClose = () => {
    setName(restaurant.name);
    setDescription(restaurant.description);
    setIsOpenStatus(restaurant.isOpen);
    setWorkStartTime(restaurant.workStartTime);
    setWorkEndTime(restaurant.workEndTime);
    setBannerFile(null);
    setBannerPreview(null);
    setUploadError('');
    onClose();
  };

  const hasChanges = () => {
    return (
      name !== restaurant.name ||
      description !== restaurant.description ||
      isOpenStatus !== restaurant.isOpen ||
      workStartTime !== restaurant.workStartTime ||
      workEndTime !== restaurant.workEndTime ||
      bannerFile !== null
    );
  };

  const currentBannerUrl = bannerPreview || imageIdToUrl(restaurant.bannerImageId);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('modals.editRestaurant.title')}>
      <form onSubmit={handleSubmit} className="modal-form">
        <div className="modal-form-group">
          <label htmlFor="name">{t('modals.editRestaurant.name')}</label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading || uploading}
          />
        </div>

        <div className="modal-form-group">
          <label htmlFor="description">{t('modals.editRestaurant.description')}</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={loading || uploading}
            rows={4}
          />
        </div>

        <div className="modal-form-group">
          <label htmlFor="isOpen">{t('modals.editRestaurant.isOpen')}</label>
          <select
            id="isOpen"
            value={isOpenStatus.toString()}
            onChange={(e) => setIsOpenStatus(e.target.value === 'true')}
            disabled={loading || uploading}
          >
            <option value="true">{t('modals.editRestaurant.open')}</option>
            <option value="false">{t('modals.editRestaurant.closed')}</option>
          </select>
        </div>

        <div className="modal-form-group">
          <label htmlFor="workStartTime">{t('modals.editRestaurant.workStartTime')}</label>
          <input
            id="workStartTime"
            type="time"
            value={workStartTime}
            onChange={(e) => setWorkStartTime(e.target.value)}
            disabled={loading || uploading}
          />
        </div>

        <div className="modal-form-group">
          <label htmlFor="workEndTime">{t('modals.editRestaurant.workEndTime')}</label>
          <input
            id="workEndTime"
            type="time"
            value={workEndTime}
            onChange={(e) => setWorkEndTime(e.target.value)}
            disabled={loading || uploading}
          />
        </div>

        <div className="modal-form-group">
          <label>{t('modals.editRestaurant.bannerImage')}</label>

          {/* Current or preview image */}
          {currentBannerUrl && (
            <div style={{
              marginBottom: '1rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              overflow: 'hidden',
              maxWidth: '100%'
            }}>
              <img
                src={currentBannerUrl}
                alt="Restaurant Banner"
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '200px',
                  objectFit: 'cover',
                  display: 'block'
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* File input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={loading || uploading}
            style={{ display: 'block', marginBottom: '0.5rem' }}
          />

          {bannerFile && (
            <button
              type="button"
              onClick={handleRemoveImage}
              className="modal-button secondary"
              disabled={loading || uploading}
              style={{ marginTop: '0.5rem', padding: '0.3rem 0.6rem', fontSize: '0.85rem' }}
            >
              {t('modals.editRestaurant.removeImage')}
            </button>
          )}

          <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
            {t('modals.editRestaurant.imageHint')}
          </small>

          {uploadError && (
            <div style={{
              color: '#dc3545',
              fontSize: '0.85rem',
              marginTop: '0.5rem',
              padding: '0.5rem',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px'
            }}>
              {uploadError}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button
            type="button"
            onClick={handleClose}
            className="modal-button secondary"
            disabled={loading || uploading}
          >
            {t('modals.editRestaurant.cancel')}
          </button>
          <button
            type="submit"
            className="modal-button primary"
            disabled={loading || uploading || !hasChanges()}
          >
            {uploading
              ? t('modals.editRestaurant.uploading')
              : loading
                ? t('modals.editRestaurant.saving')
                : t('modals.editRestaurant.save')
            }
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditRestaurantModal;
